import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Clock, Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/i18n'
import { useWatches } from '@/hooks/useWatches'
import { publicWatches } from '@/lib/catalog'
import {
  clearRecentSearches,
  highlight,
  pushRecentSearch,
  readRecentSearches,
  searchWatches,
} from '@/lib/search'
import type { Watch } from '@/types'

const SUGGESTION_LIMIT = 6

/** Render one field with the matched letters marked. */
function Marked({ text, query }: { text: string; query: string }) {
  return (
    <>
      {highlight(text, query).map((seg, i) =>
        seg.hit ? (
          <mark key={i} className="bg-transparent text-primary font-medium">
            {seg.text}
          </mark>
        ) : (
          <span key={i}>{seg.text}</span>
        ),
      )}
    </>
  )
}

interface PanelProps {
  query: string
  results: Watch[]
  total: number
  activeIndex: number
  recent: string[]
  onPick: (watch: Watch) => void
  onPickRecent: (q: string) => void
  onClearRecent: () => void
  onSeeAll: () => void
  onHover: (index: number) => void
  className?: string
}

/**
 * The suggestion list. Identical in the inline field and the header overlay —
 * one renderer, so the two entry points can never drift apart.
 */
function SearchPanel({
  query,
  results,
  total,
  activeIndex,
  recent,
  onPick,
  onPickRecent,
  onClearRecent,
  onSeeAll,
  onHover,
  className,
}: PanelProps) {
  const { t, tf, formatPrice } = useI18n()
  const trimmed = query.trim()

  // ── Nothing typed yet: offer the shortcuts, not an empty box ─────────────
  if (!trimmed) {
    if (!recent.length) return null
    return (
      <div className={cn('bg-surface text-text border border-border shadow-float rounded-squircle', className)}>
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <span className="eyebrow">{t('search.recent')}</span>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClearRecent}
            className="text-[11px] uppercase tracking-[0.14em] text-text-faint hover:text-text transition-colors"
          >
            {t('search.clearRecent')}
          </button>
        </div>
        <ul className="pb-2">
          {recent.map((q) => (
            <li key={q}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onPickRecent(q)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-text-muted hover:bg-surface-2 hover:text-text transition-colors"
              >
                <Clock size={14} strokeWidth={1.5} className="text-text-faint shrink-0" />
                {q}
              </button>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  // ── Typed, matched nothing ───────────────────────────────────────────────
  if (!results.length) {
    return (
      <div className={cn('bg-surface text-text border border-border shadow-float rounded-squircle p-6', className)}>
        <p className="text-sm text-text">{t('search.noResults', { q: trimmed })}</p>
        <p className="text-[13px] text-text-muted mt-1.5">{t('search.noResultsBody')}</p>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onSeeAll}
          className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-primary hover:text-primary-hover transition-colors"
        >
          {t('search.browseAll')}
          <ArrowRight size={14} strokeWidth={1.5} />
        </button>
      </div>
    )
  }

  // ── Matches ──────────────────────────────────────────────────────────────
  return (
    <div
      className={cn(
        'bg-surface text-text border border-border shadow-float rounded-squircle overflow-hidden',
        className,
      )}
      role="listbox"
    >
      <ul className="max-h-[min(60vh,420px)] overflow-y-auto scrollbar-thin">
        {results.map((w, i) => (
          <li key={w.id}>
            <button
              type="button"
              role="option"
              aria-selected={i === activeIndex}
              onMouseEnter={() => onHover(i)}
              // mousedown would blur the input before the click lands
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onPick(w)}
              className={cn(
                'w-full flex items-center gap-3.5 px-4 py-3 text-left transition-colors',
                i === activeIndex ? 'bg-surface-2' : 'hover:bg-surface-2',
              )}
            >
              <img
                src={w.image}
                alt=""
                loading="lazy"
                className="h-12 w-12 shrink-0 object-cover rounded-squircle bg-surface-2"
              />
              <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                <span className="block font-display text-[19px] leading-tight text-text truncate">
                  <Marked text={tf(w, 'title')} query={query} />
                </span>
                <span className="text-[10px] uppercase tracking-[0.14em] text-text-faint truncate">
                  <Marked text={`${w.reference} · ${w.maison}`} query={query} />
                </span>
              </span>
              <span className="shrink-0 text-[13px] text-text-muted">{formatPrice(w.price)}</span>
            </button>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={onSeeAll}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 border-t border-border text-[11px] uppercase tracking-[0.16em] text-text-muted hover:text-text hover:bg-surface-2 transition-colors"
      >
        {t('search.seeAll', { count: total })}
        <ArrowRight size={14} strokeWidth={1.5} />
      </button>
    </div>
  )
}

/** Everything the two entry points share: state, ranking, keyboard handling. */
function useSearchController(onDone?: () => void) {
  const navigate = useNavigate()
  const { watches, collections } = useWatches()
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(-1)
  const [recent, setRecent] = useState<string[]>(() => readRecentSearches())

  const pool = useMemo(() => publicWatches(watches), [watches])

  const hits = useMemo(
    () => searchWatches(pool, query, { collections }),
    [pool, query, collections],
  )
  const results = useMemo(() => hits.slice(0, SUGGESTION_LIMIT).map((h) => h.watch), [hits])

  // A new query invalidates the highlighted row.
  useEffect(() => setActiveIndex(-1), [query])

  const openWatch = useCallback(
    (watch: Watch) => {
      setRecent(pushRecentSearch(query))
      onDone?.()
      navigate(`/collection/${watch.id}`)
    },
    [navigate, onDone, query],
  )

  const seeAll = useCallback(() => {
    const q = query.trim()
    setRecent(pushRecentSearch(q))
    onDone?.()
    navigate(q ? `/collection?q=${encodeURIComponent(q)}` : '/collection')
  }, [navigate, onDone, query])

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((i) => (results.length ? (i + 1) % results.length : -1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((i) => (results.length ? (i <= 0 ? results.length - 1 : i - 1) : -1))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        // Enter on a highlighted row opens it; otherwise it runs the search.
        if (activeIndex >= 0 && results[activeIndex]) openWatch(results[activeIndex])
        else seeAll()
      } else if (e.key === 'Escape') {
        onDone?.()
      }
    },
    [activeIndex, onDone, openWatch, results, seeAll],
  )

  return {
    query,
    setQuery,
    results,
    total: hits.length,
    activeIndex,
    setActiveIndex,
    recent,
    setRecent,
    openWatch,
    seeAll,
    onKeyDown,
  }
}

/**
 * The big always-visible search field (home page). Suggestions drop under it as
 * you type — on a shop this size, showing the actual pieces beats showing a
 * list of guessed keywords.
 */
export function SearchCommand({ className, autoFocus = false }: { className?: string; autoFocus?: boolean }) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const c = useSearchController()

  useEffect(() => {
    const onPointer = (e: MouseEvent | TouchEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('touchstart', onPointer)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('touchstart', onPointer)
    }
  }, [])

  return (
    <div
      ref={wrapRef}
      data-upbo-id="search-inline"
      data-upbo-type="container"
      data-upbo-label="Search"
      className={cn('relative w-full', className)}
    >
      <div className="relative">
        <Search
          size={18}
          strokeWidth={1.5}
          className="absolute left-5 top-1/2 -translate-y-1/2 text-text-faint pointer-events-none"
        />
        <input
          type="search"
          value={c.query}
          autoFocus={autoFocus}
          onChange={(e) => {
            c.setQuery(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false)
            c.onKeyDown(e)
          }}
          placeholder={t('search.placeholder')}
          aria-label={t('search.label')}
          data-upbo-type="input"
          className={cn(
            'w-full h-14 pl-[3.25rem] pr-16 sm:pr-28 rounded-squircle bg-surface border border-border-strong',
            'text-[15px] text-text placeholder:text-text-faint',
            'focus:outline-none focus:border-text transition-colors',
            '[&::-webkit-search-cancel-button]:hidden',
          )}
        />
        {/* On a phone the word "Search" would eat the field it belongs to, so
            the button shrinks to its icon and Enter does the same job. */}
        <button
          type="button"
          onClick={c.seeAll}
          aria-label={t('search.label')}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-10 px-3 sm:px-5 grid place-items-center rounded-squircle bg-ink text-on-ink text-[11px] font-medium uppercase tracking-[0.14em] hover:bg-primary hover:text-on-primary transition-colors"
        >
          <Search size={16} strokeWidth={1.5} className="sm:hidden" />
          <span className="hidden sm:inline">{t('search.label')}</span>
        </button>
      </div>

      {open && (
        <SearchPanel
          className="absolute z-40 left-0 right-0 top-[calc(100%+8px)]"
          query={c.query}
          results={c.results}
          total={c.total}
          activeIndex={c.activeIndex}
          recent={c.recent}
          onPick={c.openWatch}
          onPickRecent={(q) => c.setQuery(q)}
          onClearRecent={() => c.setRecent(clearRecentSearches())}
          onSeeAll={c.seeAll}
          onHover={c.setActiveIndex}
        />
      )}
    </div>
  )
}

/**
 * The header entry point: a magnifier that opens the same search as a panel
 * under the bar. Present on every page, because the second question a shopper
 * has is never on the page they are looking at.
 */
export function SearchOverlay({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useI18n()
  const c = useSearchController(onClose)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return
    const timer = window.setTimeout(() => inputRef.current?.focus(), 30)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.clearTimeout(timer)
      document.body.style.overflow = prev
    }
  }, [open])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      aria-label={t('search.label')}
      data-upbo-id="search-overlay"
      data-upbo-label="Search overlay"
    >
      <div className="absolute inset-0 bg-overlay animate-fade-in" onClick={onClose} />

      <div className="relative mx-auto w-[min(92vw,720px)] mt-[10vh] animate-scale-in">
        <div className="flex items-center gap-3 bg-surface border border-border-strong rounded-squircle px-4">
          <Search size={18} strokeWidth={1.5} className="text-text-faint shrink-0" />
          <input
            ref={inputRef}
            type="search"
            value={c.query}
            onChange={(e) => c.setQuery(e.target.value)}
            onKeyDown={c.onKeyDown}
            placeholder={t('search.placeholder')}
            aria-label={t('search.label')}
            data-upbo-type="input"
            className="flex-1 h-14 bg-transparent text-[15px] text-text placeholder:text-text-faint focus:outline-none [&::-webkit-search-cancel-button]:hidden"
          />
          <button
            type="button"
            onClick={onClose}
            aria-label={t('nav.close')}
            className="h-9 w-9 grid place-items-center rounded-squircle text-text-muted hover:text-text hover:bg-surface-2 transition-colors"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        <SearchPanel
          className="mt-2"
          query={c.query}
          results={c.results}
          total={c.total}
          activeIndex={c.activeIndex}
          recent={c.recent}
          onPick={c.openWatch}
          onPickRecent={(q) => c.setQuery(q)}
          onClearRecent={() => c.setRecent(clearRecentSearches())}
          onSeeAll={c.seeAll}
          onHover={c.setActiveIndex}
        />
      </div>
    </div>
  )
}
